// Overview of the API tab: access, minutes (shared with the web app), keys and recent API videos. Keys are created
// and revoked through the manageApiKeys callable; everything else is read live from Firestore.
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, documentId, getDocs, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

import { auth, db } from '../firebase';
import usePlan, { isProPlan } from '../myPlans/usePlan';
import PlanPurchasePage from '../myPlans/PlanPurchasePage';
import { track } from '../analytics/track';
import { MAX_KEYS, QUESTIONS_PER_VIDEO, apiBaseUrl, apiVideoStatus, minutesFor } from './apiModel';

const manageApiKeys = httpsCallable(getFunctions(), 'manageApiKeys');
const STATUS_LABEL = { processing: 'Processing', ready: 'Ready', failed: 'Failed' };

const day = ts => (ts?.toDate ? ts.toDate().toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

function useApiKeys(uid) {
  const [keys, setKeys] = useState(null);
  useEffect(() => {
    if (!uid) return undefined;
    return onSnapshot(
      query(collection(db, 'users', uid, 'apiKeys'), orderBy('created_at', 'desc')),
      snap => setKeys(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => setKeys([]),
    );
  }, [uid]);
  return keys;
}

function useApiVideos(uid) {
  const [videos, setVideos] = useState(null);
  useEffect(() => {
    if (!uid) return undefined;
    return onSnapshot(
      query(collection(db, 'users', uid, 'workspaceContent'), where('origin', '==', 'api'),
        orderBy('workspace_creation_time', 'desc'), limit(20)),
      snap => setVideos(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => setVideos([]),
    );
  }, [uid]);
  return videos;
}

// Questions used per video (web chat and API together), from those videos' chat docs in one query
function useQuestionsUsed(uid, ids) {
  const [used, setUsed] = useState({});
  const idsKey = ids.join(',');
  useEffect(() => {
    const list = idsKey ? idsKey.split(',') : [];
    if (!uid || !list.length) {
      setUsed({});
      return;
    }
    getDocs(query(collection(db, 'users', uid, 'workspaceChats'), where(documentId(), 'in', list.slice(0, 30))))
      .then(snap => setUsed(Object.fromEntries(snap.docs.map(d => [d.id, d.data().messages_used || 0]))))
      .catch(() => setUsed({}));
  }, [uid, idsKey]);
  return used;
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard blocked: the text is still selectable
    }
  };
  return <button type="button" className="api-btn api-btn--ghost" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>;
}

export default function ApiOverview() {
  const plan = usePlan();
  const [showPlans, setShowPlans] = useState(false);

  if (plan === null) return <div className="api-card api-muted">Loading…</div>;
  if (!isProPlan(plan)) {
    return (
      <>
        <div className="api-card api-locked">
          <h2>The API comes with Pro</h2>
          <p>
            Process YouTube links into chapters and ask questions about your videos from your own code. It uses the same
            minutes and chat limits as the web app, and every video it processes shows up in your Workspace.
          </p>
          <button type="button" className="api-btn" onClick={() => { track('api_upgrade_clicked'); setShowPlans(true); }}>
            Upgrade to Pro
          </button>
        </div>
        {showPlans && <PlanPurchasePage source="api" onClose={() => setShowPlans(false)} />}
      </>
    );
  }
  return <ProOverview uid={auth.currentUser?.uid} plan={plan} />;
}

function ProOverview({ uid, plan }) {
  const keys = useApiKeys(uid);
  const videos = useApiVideos(uid);
  const ids = useMemo(() => (videos || []).map(v => v.id), [videos]);
  const used = useQuestionsUsed(uid, ids);
  const baseUrl = apiBaseUrl(window.location.origin);
  const limitMinutes = plan.creditsLimit || 0;
  const left = Math.max(limitMinutes - (plan.creditsUsed || 0), 0);

  return (
    <div>
      <h2 className="api-section-title">Access</h2>
      <div className="api-card">
        <div className="api-row">
          <div>
            <div className="api-label">Base URL</div>
            <div className="api-sub">Send your key as <code>Authorization: Bearer …</code> <Link to="/api/docs">Read the docs</Link></div>
          </div>
          <div className="api-code-row"><code className="api-code">{baseUrl}</code><CopyButton text={baseUrl} /></div>
        </div>
        <div className="api-row">
          <div>
            <div className="api-label">Minutes left</div>
            <div className="api-sub">
              Shared with the web app. Questions don't use minutes; each video allows {QUESTIONS_PER_VIDEO}, counted together with web chat.
            </div>
          </div>
          <div className="api-value">{left.toLocaleString()} / {limitMinutes.toLocaleString()}</div>
        </div>
      </div>

      <h2 className="api-section-title">Keys</h2>
      <KeysCard keys={keys} />

      <h2 className="api-section-title">Recent API videos</h2>
      <VideosCard videos={videos} used={used} />
    </div>
  );
}

function KeysCard({ keys }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [newKey, setNewKey] = useState(null);
  const full = (keys?.length || 0) >= MAX_KEYS;

  const create = async e => {
    e.preventDefault();
    if (busy || full) return;
    setBusy(true);
    setError('');
    try {
      const { data } = await manageApiKeys({ action: 'create', name });
      setNewKey(data);
      setName('');
      track('api_key_created');
    } catch (err) {
      setError(err?.message || 'The key could not be created. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async key => {
    if (!window.confirm(`Revoke "${key.name}"? Anything using it stops working within a minute.`)) return;
    setError('');
    try {
      await manageApiKeys({ action: 'revoke', keyId: key.id });
      track('api_key_revoked');
    } catch (err) {
      setError(err?.message || 'The key could not be revoked. Try again.');
    }
  };

  return (
    <div className="api-card">
      {keys === null && <div className="api-muted">Loading…</div>}
      {keys?.length === 0 && <div className="api-muted">No keys yet. Create one to start.</div>}
      {keys?.length > 0 && (
        <table className="api-table">
          <thead>
            <tr><th>Name</th><th>Key</th><th>Created</th><th>Last used</th><th aria-label="Actions" /></tr>
          </thead>
          <tbody>
            {keys.map(k => (
              <tr key={k.id}>
                <td data-label="Name">{k.name}</td>
                <td data-label="Key"><code className="api-code">{k.hint}</code></td>
                <td data-label="Created">{day(k.created_at)}</td>
                <td data-label="Last used">{k.last_used_at ? day(k.last_used_at) : 'Never'}</td>
                <td><button type="button" className="api-btn api-btn--danger" onClick={() => revoke(k)}>Revoke</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {newKey && (
        <div className="api-newkey" role="status">
          <div className="api-newkey-title">Copy your new key now. You won't be able to see it again.</div>
          <div className="api-code-row"><code className="api-code">{newKey.key}</code><CopyButton text={newKey.key} /></div>
          <div><button type="button" className="api-btn api-btn--ghost" onClick={() => setNewKey(null)}>I've saved it</button></div>
        </div>
      )}
      <form className="api-form" onSubmit={create}>
        <input
          className="api-input"
          value={name}
          onChange={e => setName(e.target.value)}
          maxLength={40}
          placeholder={full ? `You have the maximum of ${MAX_KEYS} keys` : 'Key name, e.g. "My server"'}
          disabled={full}
          aria-label="Key name"
        />
        <button type="submit" className="api-btn" disabled={busy || full}>{busy ? 'Creating…' : 'Create key'}</button>
      </form>
      {error && <p className="api-error" role="alert">{error}</p>}
    </div>
  );
}

function VideosCard({ videos, used }) {
  if (videos === null) return <div className="api-card api-muted">Loading…</div>;
  if (!videos.length) {
    return <div className="api-card api-muted">Videos you process through the API show up here and in your Workspace.</div>;
  }
  return (
    <div className="api-card">
      <table className="api-table">
        <thead>
          <tr><th>Video</th><th>Status</th><th>Minutes</th><th>Questions</th><th>Added</th></tr>
        </thead>
        <tbody>
          {videos.map(v => {
            const status = apiVideoStatus(v);
            return (
              <tr key={v.id}>
                <td data-label="Video">
                  <Link to={`/workspace/${v.id.replace(/^workspace-/, '')}`}>{v.yt_video_title || v.workspace_title || 'Untitled'}</Link>
                </td>
                <td data-label="Status"><span className={`api-status api-status--${status}`}>{STATUS_LABEL[status]}</span></td>
                <td data-label="Minutes">{status === 'failed' ? 0 : minutesFor(v.video_duration_in_seconds)}</td>
                <td data-label="Questions">{used[v.id] || 0} / {QUESTIONS_PER_VIDEO}</td>
                <td data-label="Added">{day(v.workspace_creation_time)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
